import { computed, inject, Injectable, signal } from '@angular/core';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { TourCompletion } from './tour-completion';
import { Tour, TourStep } from './tour.model';
import { findTour } from './tours';

/** Le parcours en cours, réduit aux étapes que l'utilisateur a le droit de voir. */
interface ActiveTour {
  tour: Tour;
  steps: TourStep[];
}

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);
  private readonly completion = inject(TourCompletion);
  private readonly active = signal<ActiveTour | null>(null);
  private readonly index = signal(0);

  readonly tour = computed(() => this.active()?.tour ?? null);
  readonly steps = computed(() => this.active()?.steps ?? []);
  readonly stepIndex = this.index.asReadonly();
  readonly step = computed(() => this.steps()[this.index()] ?? null);
  readonly isFirst = computed(() => this.index() === 0);
  readonly isLast = computed(() => this.index() === this.steps().length - 1);

  /** Les étapes de ce parcours que l'utilisateur connecté a le droit de voir. */
  visibleSteps(tour: Tour): TourStep[] {
    return tour.steps.filter(
      (step) => !step.roles || this.authenticatedUserService.hasAnyRole(step.roles),
    );
  }

  /** Lance un parcours par sa clé ; `false` si le front ne le connaît pas ou n'a rien à montrer. */
  start(id: string): boolean {
    const tour = findTour(id);
    const steps = tour ? this.visibleSteps(tour) : [];
    if (!tour || !steps.length) return false;
    this.active.set({ tour, steps });
    this.index.set(0);
    return true;
  }

  /** Passe à l'étape suivante ; après la dernière, le parcours est suivi, et les onglets le savent. */
  next(): void {
    if (this.isLast()) {
      const tour = this.tour();
      if (tour) this.completion.signal(tour.id);
      this.stop();
      return;
    }
    this.index.update((index) => index + 1);
  }

  previous(): void {
    this.index.update((index) => Math.max(0, index - 1));
  }

  stop(): void {
    this.active.set(null);
    this.index.set(0);
  }
}
